import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { I18nService } from 'src/app/services/i18n.service';

export interface NearbyQuery {
  lat: number;
  lng: number;
  km: number;
}

const RADIUS_KEY = 'nearby_radius_km';
export const NEARBY_RADII = [2, 5, 10, 25, 50];
const DEFAULT_RADIUS = 10;

// « Offres près de moi » (/pres-de-moi) : position demandée sur un bouton (ou reprise si déjà autorisée) et rayon choisi.
// La position, arrondie à ~100 m, ne sert qu'à la recherche : elle n'est ni stockée ni envoyée ailleurs.
@Component({
    selector: 'app-nearby-panel',
    templateUrl: './nearby-panel.component.html',
    styleUrls: ['./nearby-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NearbyPanelComponent implements OnInit {

  @Output() nearbySearch = new EventEmitter<NearbyQuery>();

  readonly radii = NEARBY_RADII;
  radius = DEFAULT_RADIUS;
  locating = false;
  error = '';
  private position: { lat: number; lng: number } | null = null;

  constructor(
    private i18n: I18nService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.radius = this.savedRadius();
    // Autorisation déjà donnée (visite précédente) : recherche lancée sans clic
    navigator.permissions?.query({ name: 'geolocation' as PermissionName })
      .then(status => status.state === 'granted' && this.locate())
      .catch(() => { });
  }

  get hasPosition(): boolean {
    return this.position !== null;
  }

  locate(): void {
    this.error = '';
    if (!isPlatformBrowser(this.platformId) || !navigator.geolocation) {
      this.error = this.i18n.t('nearby.unavailable');
      return;
    }
    this.locating = true;
    navigator.geolocation.getCurrentPosition(position => {
      this.locating = false;
      const round = (n: number) => Math.round(n * 1000) / 1000;
      this.position = { lat: round(position.coords.latitude), lng: round(position.coords.longitude) };
      this.emit();
    }, error => {
      this.locating = false;
      this.error = this.i18n.t(error.code === error.PERMISSION_DENIED ? 'nearby.denied' : 'nearby.unavailable');
    }, { enableHighAccuracy: false, timeout: 15000, maximumAge: 5 * 60 * 1000 });
  }

  setRadius(km: number): void {
    this.radius = km;
    try {
      localStorage.setItem(RADIUS_KEY, String(km));
    } catch { }
    this.emit();
  }

  private emit(): void {
    if (this.position) {
      this.nearbySearch.emit({ ...this.position, km: this.radius });
    }
  }

  private savedRadius(): number {
    try {
      const saved = Number(localStorage.getItem(RADIUS_KEY));
      return NEARBY_RADII.includes(saved) ? saved : DEFAULT_RADIUS;
    } catch {
      return DEFAULT_RADIUS;
    }
  }
}
